import { Tooltip } from '@components/tooltip/Tooltip';
import { Carousel } from '@shared/components/carousel/Carousel';
import { Icon } from '@shared/components/icon/Icon';
import { SimpleBar } from '@shared/components/simpleBar/simpleBar';
import { errorToast } from '@shared/components/toasts/Toasts';
import cx from 'classnames';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import styles from './ImageUploader.scss';

const MAX_IMAGES = 1;
const MAX_SIZE_MB = 5;

const IMAGE_GRID_MIN_EMPTY_SLOTS = 3;

/** Custom payload type so file drops are not confused with in-grid reorder drags. */
const IMAGE_GRID_REORDER_PAYLOAD_TYPE =
  'application/x-image-grid-reorder-index';

const isReorderImageDrag = (e: React.DragEvent): boolean =>
  Array.from(e.dataTransfer.types).includes(IMAGE_GRID_REORDER_PAYLOAD_TYPE);

export type ImageUploaderVariant = 'default' | 'imageGrid';

export interface ImageUploaderProps {
  imageUrl: string | null;
  /**
   * Ordered preview URLs. When non-empty, drives the preview.
   * When omitted or empty, falls back to a single `imageUrl`.
   */
  imageUrls?: string[];
  onClear?: () => void;
  /**
   * When set, each thumbnail shows a remove control; Clear removes all photos (`onClear`).
   * When omitted, Clear calls `onClear` only.
   */
  onRemoveImagesAt?: (indices: number[]) => void;
  /**
   * When `variant` is `imageGrid` and there are at least two images,
   * thumbnails become draggable to change order (`fromIndex` → `toIndex`).
   */
  onReorderImages?: (fromIndex: number, toIndex: number) => void;
  onImageUpload: (objectKey: string) => void;
  /**
   * Returns a presigned PUT URL. `ImageUploader` passes the selected `file` on each call;
   * use `file?` in your callback when the URL does not depend on it. When `uploadContentType`
   * is set, the PUT must send that exact `Content-Type` header (S3 presigned constraint).
   */
  getUploadUrl: (file?: File) => Promise<{
    signedUploadUrl: string;
    objectKey: string;
    uploadContentType?: string;
  }>;
  multiple?: boolean;
  /**
   * Max images when `multiple` is false. When `multiple` is true, use
   * `maxSelectableImages` to cap; otherwise there is no cap.
   */
  maxImages?: number;
  /**
   * When set, caps total images even with `multiple` (e.g. a fixed grid of table photos).
   * When omitted with `multiple`, there is no cap.
   */
  maxSelectableImages?: number;
  maxSizeMB?: number;
  /** When non-empty, clear control matches the assign-tables “Clear selection” control and shows this tooltip. */
  clearControlTooltipContent?: string;
  /** When set, shown left of the clear control as `{photosHeading} ({count})`. */
  photosHeading?: string;
  variant?: ImageUploaderVariant;
  /** `data-testid` on the slot row when `variant` is `imageGrid`. */
  slotRowTestId?: string;
  /** Overrides default `data-testid` on the hidden file input. */
  uploadInputTestId?: string;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  imageUrl,
  imageUrls,
  onClear,
  onRemoveImagesAt,
  onReorderImages,
  onImageUpload,
  getUploadUrl,
  multiple = false,
  maxImages = MAX_IMAGES,
  maxSelectableImages,
  maxSizeMB = MAX_SIZE_MB,
  clearControlTooltipContent,
  photosHeading,
  variant = 'default',
  slotRowTestId,
  uploadInputTestId,
}) => {
  const effectiveMaxImages =
    maxSelectableImages != null
      ? maxSelectableImages
      : multiple
        ? Number.POSITIVE_INFINITY
        : maxImages;

  const uploadCapMessageCount = Number.isFinite(effectiveMaxImages)
    ? effectiveMaxImages
    : maxImages;

  const inputTestId = uploadInputTestId ?? 'image-upload-input';
  /**
   * `variant` switches between two layouts after loading state is resolved:
   * - **`default`**: carousel / single-image preview with the standard drag surface and clear flow.
   * - **`imageGrid`**: fixed-width slot grid for several images in one row (see below).
   *
   * **`imageGrid` features**
   * - Renders one square slot per existing preview (`imageUrls` / `imageUrl`), capped by `effectiveMaxImages`.
   * - Keeps a minimum number of dashed “add” placeholder slots plus a primary **Add** control (until at cap).
   * - Wrap and empty slots accept **file drag-and-drop** uploads; reorder drags use a dedicated MIME type so they do not open the file picker path.
   * - With `onReorderImages` and at least two images, filled slots are **draggable** to reorder (calls `onReorderImages(from, to)`).
   * - With `onRemoveImagesAt`, each filled slot can show a **remove** control for that index.
   * - Optional `slotRowTestId` on the grid row for tests and page wiring.
   */
  const isGridVariant = variant === 'imageGrid';
  const clearControlTooltipText = clearControlTooltipContent?.trim() ?? '';
  const hasClearControlTooltip = clearControlTooltipText.length > 0;
  const previewUrls =
    imageUrls != null && imageUrls.length > 0
      ? imageUrls
      : imageUrl != null && imageUrl !== ''
        ? [imageUrl]
        : [];
  const hasImage = previewUrls.length > 0;
  const imagesCount = previewUrls.length;
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [activePreviewIndex, setActivePreviewIndex] = useState(0);
  const [gridDragOverIndex, setGridDragOverIndex] = useState<number | null>(
    null,
  );
  const [gridDraggingIndex, setGridDraggingIndex] = useState<number | null>(
    null,
  );

  const usesPerThumbRemove = onRemoveImagesAt != null;
  const canSwitchPreview = previewUrls.length > 1;

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset active preview when thumbnail count changes
  useEffect(() => {
    setActivePreviewIndex(0);
  }, [previewUrls.length]);

  const safeActivePreviewIndex = Math.min(
    activePreviewIndex,
    Math.max(0, previewUrls.length - 1),
  );

  const addMoreThumbAriaLabel =
    effectiveMaxImages !== Number.POSITIVE_INFINITY &&
    imagesCount >= effectiveMaxImages
      ? previewUrls.length > 1
        ? 'Replace images'
        : 'Replace image'
      : 'Add images';

  const handleClearPhotosClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClear?.();
  };

  const handleRemoveThumbClick = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    if (onRemoveImagesAt == null) return;
    onRemoveImagesAt([index]);
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const uploadImage = (
    signedUploadUrl: string,
    file: File,
    uploadContentType?: string,
  ) =>
    fetch(signedUploadUrl, {
      method: 'PUT',
      body: file,
      ...(uploadContentType != null
        ? { headers: { 'Content-Type': uploadContentType } }
        : {}),
    });

  const processFiles = async (files: File[]) => {
    const validFiles = files.filter(
      (file) => file.size <= maxSizeMB * 1024 * 1024,
    );
    if (validFiles.length < files.length) {
      errorToast({
        message: `Some files were too large. Max size is ${maxSizeMB}MB.`,
      });
    }

    const uploadSlotCount = (() => {
      if (imagesCount >= effectiveMaxImages) {
        if (!multiple) {
          return 1;
        }
        if (Number.isFinite(effectiveMaxImages)) {
          return 0;
        }
        return Number.POSITIVE_INFINITY;
      }
      return effectiveMaxImages - imagesCount;
    })();

    const toUpload = Number.isFinite(uploadSlotCount)
      ? validFiles.slice(0, uploadSlotCount)
      : validFiles;
    if (toUpload.length === 0) {
      if (imagesCount >= effectiveMaxImages && files.length > 0) {
        errorToast({
          message: `You can only upload up to ${uploadCapMessageCount} images.`,
        });
      }
      return;
    }

    try {
      for (const file of toUpload) {
        const { signedUploadUrl, objectKey, uploadContentType } =
          await getUploadUrl(file);
        await uploadImage(signedUploadUrl, file, uploadContentType);
        onImageUpload(objectKey);
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      errorToast({
        message: 'Failed to upload images. Please try again.',
      });
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    await processFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isGridVariant && isReorderImageDrag(e)) {
      e.dataTransfer.dropEffect = 'move';
      return;
    }
    if (imagesCount < effectiveMaxImages) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isGridVariant && isReorderImageDrag(e)) {
      return;
    }
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isGridVariant && isReorderImageDrag(e)) {
      setGridDragOverIndex(null);
      setGridDraggingIndex(null);
      return;
    }
    setIsDragging(false);
    if (imagesCount >= effectiveMaxImages) return;
    const files = Array.from(e.dataTransfer.files).filter((file) =>
      file.type.startsWith('image/'),
    );
    if (files.length > 0) {
      await processFiles(files);
    }
  };

  const handleActivate = () => {
    if (imagesCount < effectiveMaxImages) {
      handleClick();
    } else {
      errorToast({
        message: `The limit of ${uploadCapMessageCount} images was already reached.`,
      });
    }
  };

  const handleAddThumbClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (effectiveMaxImages === 0) {
      errorToast({
        message: `The limit of ${uploadCapMessageCount} images was already reached.`,
      });
      return;
    }
    handleClick();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleActivate();
    }
  };

  if (isGridVariant) {
    const isUploadFull = imagesCount >= effectiveMaxImages;
    const placeholderCount = Math.max(
      0,
      IMAGE_GRID_MIN_EMPTY_SLOTS - previewUrls.length,
    );
    const atCapAriaLabel = `Maximum of ${uploadCapMessageCount} table photos. Remove one to add another.`;
    const atCapTitle = `Maximum of ${uploadCapMessageCount} table photos per table`;
    const canReorderImages = onReorderImages != null && previewUrls.length >= 2;

    const blockReorderDrop = (e: React.DragEvent) => {
      if (!isReorderImageDrag(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'none';
    };

    return (
      <>
        {/* biome-ignore lint/a11y/noStaticElementInteractions: file drop target (same pattern as default uploader drag surface) */}
        <div
          className={cx(styles.imageGridWrap, {
            [styles.imageGridWrapDragging]: isDragging,
          })}
          role="presentation"
          onDragEnter={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className={styles.imageGrid} data-testid={slotRowTestId}>
            {previewUrls.map((url, index) => (
              <Fragment key={`${index}-${url}`}>
                {/* biome-ignore lint/a11y/noStaticElementInteractions: native drag-and-drop reorder targets */}
                <div
                  className={cx(
                    styles.imageGridSlot,
                    styles.imageGridSlotFilled,
                    {
                      [styles.imageGridSlotReorderable]: canReorderImages,
                      [styles.imageGridSlotDragging]:
                        canReorderImages && gridDraggingIndex === index,
                      [styles.imageGridSlotDropTarget]:
                        canReorderImages &&
                        gridDragOverIndex === index &&
                        gridDraggingIndex !== index,
                    },
                  )}
                  style={url ? { backgroundImage: `url(${url})` } : undefined}
                  draggable={canReorderImages}
                  aria-grabbed={
                    canReorderImages && gridDraggingIndex === index
                      ? true
                      : undefined
                  }
                  title={
                    canReorderImages
                      ? 'Drag to reorder table photos'
                      : undefined
                  }
                  onDragStart={(e) => {
                    if (!canReorderImages) return;
                    if ((e.target as HTMLElement).closest('button')) {
                      e.preventDefault();
                      return;
                    }
                    e.dataTransfer.setData(
                      IMAGE_GRID_REORDER_PAYLOAD_TYPE,
                      String(index),
                    );
                    e.dataTransfer.effectAllowed = 'move';
                    setGridDraggingIndex(index);
                  }}
                  onDragEnd={() => {
                    setGridDraggingIndex(null);
                    setGridDragOverIndex(null);
                  }}
                  onDragOver={(e) => {
                    if (!canReorderImages || !isReorderImageDrag(e)) return;
                    e.preventDefault();
                    e.stopPropagation();
                    e.dataTransfer.dropEffect = 'move';
                    setGridDragOverIndex(index);
                  }}
                  onDragLeave={(e) => {
                    if (!canReorderImages || !isReorderImageDrag(e)) return;
                    const related = e.relatedTarget as Node | null;
                    if (related && e.currentTarget.contains(related)) return;
                    setGridDragOverIndex((prev) =>
                      prev === index ? null : prev,
                    );
                  }}
                  onDrop={(e) => {
                    if (!canReorderImages || !isReorderImageDrag(e)) return;
                    e.preventDefault();
                    e.stopPropagation();
                    const fromStr = e.dataTransfer.getData(
                      IMAGE_GRID_REORDER_PAYLOAD_TYPE,
                    );
                    const from = Number.parseInt(fromStr, 10);
                    if (Number.isNaN(from) || from === index) {
                      setGridDragOverIndex(null);
                      setGridDraggingIndex(null);
                      return;
                    }
                    onReorderImages(from, index);
                    setGridDragOverIndex(null);
                    setGridDraggingIndex(null);
                  }}
                >
                  {usesPerThumbRemove ? (
                    <button
                      type="button"
                      className={styles.imageGridSlotRemove}
                      aria-label="Remove table image"
                      onClick={(e) => handleRemoveThumbClick(e, index)}
                    >
                      <Icon
                        name="close"
                        className={styles.imageGridSlotRemoveIcon}
                        aria-hidden
                      />
                    </button>
                  ) : null}
                </div>
              </Fragment>
            ))}
            {Array.from({ length: placeholderCount }, (_, slotIndex) => (
              <button
                // biome-ignore lint/suspicious/noArrayIndexKey: empty slots are anonymous; list length is small and stable per row
                key={`image-grid-empty-${slotIndex}`}
                type="button"
                className={cx(
                  styles.imageGridSlot,
                  styles.imageGridSlotEmpty,
                  styles.imageGridSlotPlaceholder,
                )}
                aria-label={isUploadFull ? atCapAriaLabel : 'Add table photo'}
                disabled={isUploadFull}
                title={isUploadFull ? atCapTitle : undefined}
                onClick={handleClick}
                onDragOver={blockReorderDrop}
              >
                <Icon name="plus" className={styles.imageGridPlusIcon} />
              </button>
            ))}
            <button
              type="button"
              className={cx(
                styles.imageGridSlot,
                styles.imageGridSlotEmpty,
                styles.imageGridSlotAddPrimary,
              )}
              aria-label={
                isUploadFull
                  ? atCapAriaLabel
                  : previewUrls.length === 0
                    ? 'Add table photos'
                    : 'Add another table photo'
              }
              disabled={isUploadFull}
              title={isUploadFull ? atCapTitle : undefined}
              onClick={handleClick}
              onDragOver={blockReorderDrop}
            >
              <Icon name="plus" className={styles.imageGridPlusIcon} />
              <span className={styles.imageGridAddLabel}>Add</span>
            </button>
          </div>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className={styles.imageGridHiddenInput}
          onChange={handleFileChange}
          multiple={multiple}
          data-testid={inputTestId}
        />
      </>
    );
  }

  if (hasImage) {
    const clearPhotosCopy =
      previewUrls.length > 1 ? 'Clear photos' : 'Clear photo';
    const clearAriaLabel = hasClearControlTooltip ? clearPhotosCopy : undefined;
    const addThumbDisabled = effectiveMaxImages === 0;

    const clearPhotosButton = (
      <button
        type="button"
        aria-label={clearAriaLabel}
        className={cx(
          hasClearControlTooltip
            ? styles.clearButtonClearSelection
            : styles.clearButton,
        )}
        onClick={handleClearPhotosClick}
      >
        {hasClearControlTooltip ? (
          <>
            <span aria-hidden>
              <Icon name="rotateCcw" />
            </span>
            <span aria-hidden>{clearPhotosCopy}</span>
          </>
        ) : (
          clearPhotosCopy
        )}
      </button>
    );

    return (
      <div className={styles.previewWrapper}>
        <div className={styles.previewMainRow}>
          <div className={styles.previewMain}>
            <Carousel
              className={styles.previewCarouselRoot}
              backdropClassName={styles.previewCarouselBackdrop}
              slides={previewUrls.map((url, index) => ({
                key: `${index}-${url}`,
                backgroundImage: `url(${url})`,
                src: url,
              }))}
              variant="fullWidth"
              fullWidthHeightMode="fitContent"
              autoAdvance={false}
              activeIndex={safeActivePreviewIndex}
              onActiveIndexChange={setActivePreviewIndex}
              showDotNavigation={false}
              fullWidthImageAlt={`Photo preview ${safeActivePreviewIndex + 1} of ${previewUrls.length}`}
            />
          </div>
          <div className={styles.previewThumbnailsAside}>
            <button
              type="button"
              className={styles.previewThumbAdd}
              aria-label={addMoreThumbAriaLabel}
              disabled={addThumbDisabled}
              onClick={handleAddThumbClick}
            >
              <Icon name="plus" className={styles.previewThumbAddIcon} />
            </button>
            <SimpleBar className={styles.previewThumbnailsSimpleBar}>
              <ul
                className={styles.previewThumbnailsList}
                data-testid="image-uploader-thumbnails"
              >
                {previewUrls.map((url, index) => (
                  <li
                    key={url}
                    className={cx(styles.previewThumb, {
                      [styles.previewThumbActive]:
                        index === safeActivePreviewIndex,
                    })}
                  >
                    {canSwitchPreview ? (
                      <button
                        type="button"
                        className={styles.previewThumbImageButton}
                        aria-label={`Show image ${index + 1} in preview`}
                        draggable={false}
                        onClick={() => setActivePreviewIndex(index)}
                      >
                        <div
                          className={styles.previewThumbImage}
                          style={{ backgroundImage: `url(${url})` }}
                          aria-hidden
                        />
                      </button>
                    ) : (
                      <div
                        className={styles.previewThumbImage}
                        style={{ backgroundImage: `url(${url})` }}
                        aria-hidden
                      />
                    )}
                    {usesPerThumbRemove ? (
                      <button
                        type="button"
                        className={styles.previewThumbRemoveButton}
                        aria-label={`Remove image ${index + 1}`}
                        draggable={false}
                        onClick={(e) => handleRemoveThumbClick(e, index)}
                      >
                        <Icon
                          name="close"
                          className={styles.previewThumbRemoveIcon}
                          aria-hidden
                        />
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </SimpleBar>
          </div>
        </div>
        <div
          className={cx(styles.previewHeader, {
            [styles.previewHeaderWithCountLabel]: Boolean(photosHeading),
          })}
        >
          {photosHeading ? (
            <span className={styles.previewPhotosHeading}>
              {photosHeading} ({imagesCount})
            </span>
          ) : null}
          {hasClearControlTooltip ? (
            <Tooltip
              content={clearControlTooltipText}
              idPrefix="re-img-tooltip"
              tooltipClassName={styles.clearPhotoTooltip}
            >
              {clearPhotosButton}
            </Tooltip>
          ) : (
            clearPhotosButton
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
          multiple={multiple}
          data-testid={inputTestId}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`${styles.uploader} ${isDragging ? styles.uploaderDragging : ''}`}
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      aria-label="Upload image"
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
        multiple={multiple}
        data-testid={inputTestId}
      />
      <span className={styles.centerContent}>
        <Icon name="imagePlaceholder" className={styles.uploadIcon} />
        <span className={styles.uploadText}>Drag & drop images here</span>
      </span>
    </button>
  );
};
